import {FC, useState, ChangeEvent} from 'react'
import {gql} from '@apollo/client'
import {useDispatch} from 'react-redux'
import {Modal} from 'jbc-front/components/presenters/ui/Modal'
import {ButtonRow} from 'jbc-front/components/presenters/layout/ButtonRow'
import Button from 'jbc-front/components/Button'
import {Label} from 'jbc-front/components/presenters/form/Label'
import {notifySuccess} from 'store/actions/notify'
import {asyncError} from 'store/actions/asyncError'
import {useMutation} from 'hooks/graphql'
import {useSelector} from 'hooks/redux'
import styles from './RemindModal.scss'

// @ts-ignore
import {getMainOfficeContactInfo} from 'offices/utils'

const REMIND_CONTRACT = gql`
  mutation remindEmployeeDocumentContract($input: RemindEmployeeDocumentContractInput!) {
    remindEmployeeDocumentContract(input: $input) {
      employeeDocumentContractRequest {
        id
      }
    }
  }
`

type SenderInfoType = 'current' | 'original'

type SenderInfoRadioFieldProps = {
  currentUserInfo: string
  contractOriginalSenderInfo: string
  selectValue: SenderInfoType
  setSelectValue: (value: SenderInfoType) => void
}

const SenderInfoRadioField: FC<SenderInfoRadioFieldProps> = ({
  currentUserInfo,
  contractOriginalSenderInfo,
  selectValue,
  setSelectValue,
}) => {
  const senderInfos = [
    {value: 'current', label: currentUserInfo},
    {value: 'original', label: contractOriginalSenderInfo},
  ]
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => setSelectValue(event.target.value as SenderInfoType)

  return (
    <>
      {senderInfos.map(({value, label}) => (
        <div className={styles.senderInfoInputLabel} key={value}>
          <label>
            <input
              type="radio"
              value={value}
              checked={selectValue === value}
              onChange={handleChange}
              className="m-radio-input"
            />
            <span className="m-radio-parts">
              {value === 'original' ? <span className={styles.annotation}>*</span> : null}
              {label}
            </span>
          </label>
          {value === 'original' ? <p className={styles.originalSenderText}>※電子契約依頼の作成者です</p> : null}
        </div>
      ))}
    </>
  )
}

type RemindModalProps = {
  employeeId: string
  contractRequestId: string
  contractOriginalSenderInfo: string | null
}

export const RemindModal: FC<RemindModalProps> = ({employeeId, contractRequestId, contractOriginalSenderInfo}) => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const handleOpen = () => setIsOpen(true)
  const handleClose = () => setIsOpen(false)

  const currentUser = useSelector((state) => state.session.currentUser)
  const currentOffice = useSelector((state) => state.client.current.office)
  const senderClientName = getMainOfficeContactInfo(currentOffice).name
  const currentUserInfo = `${senderClientName} ${currentUser.name || currentUser.employee?.display_full_name}`

  const [submitting, setSubmitting] = useState(false)
  const [selectValue, setSelectValue] = useState<SenderInfoType>('current')
  const [remindContract] = useMutation(REMIND_CONTRACT)
  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      await remindContract({variables: {input: {employeeId, contractRequestId, remindSender: selectValue}}})
      dispatch(notifySuccess('送信しました'))
    } catch (err) {
      dispatch(asyncError(err))
    }
    setSubmitting(false)
    handleClose()
  }

  return (
    <>
      <Button onClick={handleOpen}>電子契約リマインドメール送信</Button>
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <Modal.Header onClose={handleClose}>電子契約リマインドメール送信確認</Modal.Header>
        <Modal.Body>
          <form>
            <div className="u-mb20">
              <Label>差出人</Label>
              {contractOriginalSenderInfo === null || currentUserInfo === contractOriginalSenderInfo ? (
                <p className={styles.text}>{currentUserInfo}</p>
              ) : (
                <SenderInfoRadioField
                  currentUserInfo={currentUserInfo}
                  contractOriginalSenderInfo={contractOriginalSenderInfo}
                  selectValue={selectValue}
                  setSelectValue={setSelectValue}
                />
              )}
              <div className="u-mt20">
                <p className={styles.annotation}>
                  ※前回のリマインドメール送信から1時間を経過していない場合は送信されません
                </p>
                <p className={styles.annotation}>
                  ※署名完了されていない方へ1週間おきにリマインドメールが自動送信されています。追加で送信したい場合に、「送信する」を押してください。
                </p>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <ButtonRow>
            <Button onClick={handleClose}>キャンセル</Button>
            <Button primary onClick={handleSubmit} disabled={submitting}>
              送信する
            </Button>
          </ButtonRow>
        </Modal.Footer>
      </Modal>
    </>
  )
}
